import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api, { extractApiError } from '../api';
import { useAuth } from '../AuthContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import EventPlanEditDialog from './EventPlanEditDialog';

function formatEventDateUtc(dateString) {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  const day = date.getUTCDate();
  const month = date.getUTCMonth() + 1;
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes} UTC`;
}

function getPlanStatus(plan) {
  const now = Date.now();
  const startMs = Date.parse(plan.startDate);
  const endMs = Date.parse(plan.endDate);
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return 'unknown';
  if (now >= startMs && now <= endMs) return 'live';
  if (now < startMs) return 'upcoming';
  return 'ended';
}

const STATUS_COLORS = {
  live: 'success',
  upcoming: 'info',
  ended: 'default',
  unknown: 'warning',
};

export default function EventPlanner() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { environmentLabel } = useEnvironment();
  const [search, setSearch] = useState('');
  const [editDialog, setEditDialog] = useState({ open: false, plan: null });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const canEdit = ['admin', 'super_admin'].includes(user?.role);

  const { data: plans = [], isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['eventPlans'],
    queryFn: async () => (await api.get('/event-plans')).data,
    staleTime: 30_000,
  });

  const { data: assets = [] } = useQuery({
    queryKey: ['inventoryAssets'],
    queryFn: async () => (await api.get('/inventories/items')).data,
    staleTime: 5 * 60_000,
  });

  const filteredPlans = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return plans;
    return plans.filter((plan) =>
      plan.title?.toLowerCase().includes(term)
      || plan.id?.toLowerCase().includes(term)
      || plan.eventType?.toLowerCase().includes(term)
    );
  }, [plans, search]);

  const handleSave = async (definition) => {
    setSaving(true);
    try {
      if (editDialog.plan?.id) {
        await api.put(`/event-plans/${editDialog.plan.id}`, { definition });
      } else {
        await api.post('/event-plans', { definition });
      }
      await queryClient.invalidateQueries({ queryKey: ['eventPlans'] });
      setEditDialog({ open: false, plan: null });
      setSnackbar({ open: true, message: 'Event plan saved', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: extractApiError(err), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.title}"? This does not affect live EventInfo.json.`)) return;
    try {
      await api.delete(`/event-plans/${plan.id}`);
      await queryClient.invalidateQueries({ queryKey: ['eventPlans'] });
      setSnackbar({ open: true, message: 'Event plan deleted', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: extractApiError(err), severity: 'error' });
    }
  };

  const handleSyncFromLive = async () => {
    if (!window.confirm(`Import/update all events from live EventInfo.json on ${environmentLabel}? Existing plans with same ID will be overwritten.`)) {
      return;
    }
    setSyncing(true);
    try {
      const { data } = await api.post('/event-plans/sync-from-live');
      await queryClient.invalidateQueries({ queryKey: ['eventPlans'] });
      setSnackbar({
        open: true,
        message: `Synced from live: ${data.inserted} inserted, ${data.updated} updated (${data.total} total)`,
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({ open: true, message: extractApiError(err), severity: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">Event Planner</Typography>
          <Typography variant="body2" color="text.secondary">
            Plan and schedule events in {environmentLabel}. The game still reads EventInfo.json until you publish in phase 2.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh list">
            <span>
              <IconButton onClick={() => refetch()} disabled={isFetching}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          {canEdit && (
            <>
              <Button
                variant="outlined"
                startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
                onClick={handleSyncFromLive}
                disabled={syncing}
              >
                Sync from live
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setEditDialog({ open: true, plan: null })}
              >
                New event
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Planning-only mode: edits here are stored in the database and do not change what players see yet.
        Use <strong>/events</strong> for live leaderboards and reward processing.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {extractApiError(error)}
        </Alert>
      )}

      <TextField
        size="small"
        placeholder="Search by title, type, or ID"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell align="right">Tiers</TableCell>
                {canEdit && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPlans.map((plan) => {
                const status = getPlanStatus(plan);
                return (
                  <TableRow key={plan.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{plan.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{plan.id}</Typography>
                    </TableCell>
                    <TableCell>{plan.eventType}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatEventDateUtc(plan.startDate)}</Typography>
                      <Typography variant="caption" color="text.secondary">→ {formatEventDateUtc(plan.endDate)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={status} color={STATUS_COLORS[status]} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={plan.enabled ? 'enabled' : 'disabled'} variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{plan.definition?.rewardTierInfos?.length ?? 0}</TableCell>
                    {canEdit && (
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => setEditDialog({ open: true, plan })} aria-label="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(plan)} aria-label="Delete">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {!filteredPlans.length && (
                <TableRow>
                  <TableCell colSpan={canEdit ? 7 : 6} align="center" sx={{ py: 4 }}>
                    No event plans yet. Run migrations and use Sync from live, or create a new event.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <EventPlanEditDialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, plan: null })}
        eventPlan={editDialog.plan}
        assets={assets}
        onSave={handleSave}
        saving={saving}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
}
